import { z } from "zod";
import type { ServiceResult } from "../job/job.types";
import { FormDefinitionSchema } from "./form-definition-core.types";

function formatZodIssues(error: z.ZodError): string {
	return error.issues
		.map(
			(iss) =>
				`${iss.path.length ? iss.path.join(".") : "(root)"}: ${iss.message}`,
		)
		.join("; ");
}

/**
 * OpenAPI `AssetType` — nested on {@link FormDefinitionWithScope} list rows (`swagger2.json`).
 */
export const AssetTypeInScopeSchema = z.object({
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable().optional(),
	createdAt: z.string().nullable().optional(),
	createdAtOffset: z.string().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
	updatedAtOffset: z.string().nullable().optional(),
	deleted: z.boolean().optional(),
	organizationId: z.string().uuid(),
	subOrganizationId: z.string().uuid().nullable().optional(),
	name: z.string(),
	description: z.string().nullable().optional(),
	icon: z.string(),
});

/**
 * OpenAPI `SubOrganizationJobType` — items under `jobType.subOrganizationJobTypes`.
 * `subOrganization` is left loose to avoid pulling `Address` and the full `SubOrganization` graph into this module.
 */
export type SubOrganizationJobTypeInScope = {
	id?: string;
	createdBy?: string | null;
	createdAt?: string | null;
	createdAtOffset?: string | null;
	updatedBy?: string | null;
	updatedAt?: string | null;
	updatedAtOffset?: string | null;
	organizationId?: string;
	subOrganizationId?: string;
	jobTypeId?: string;
	jobType?: JobTypeInScope;
	subOrganization?: unknown;
};

/**
 * OpenAPI `JobType` — nested on form-definition list rows (`swagger2.json`).
 */
export type JobTypeInScope = {
	id: string;
	createdBy?: string | null;
	createdAt?: string | null;
	createdAtOffset?: string | null;
	updatedBy?: string | null;
	updatedAt?: string | null;
	updatedAtOffset?: string | null;
	deleted?: boolean;
	organizationId: string;
	subOrganizationJobTypes?: SubOrganizationJobTypeInScope[] | null;
	name: string;
	description?: string | null;
	icon: string;
	updatable?: boolean;
	isDefault?: boolean;
};

const SubOrganizationJobTypeInScopeSchema: z.ZodType<SubOrganizationJobTypeInScope> =
	z.lazy(() =>
		z.object({
			id: z.string().uuid().optional(),
			createdBy: z.string().uuid().nullable().optional(),
			createdAt: z.string().nullable().optional(),
			createdAtOffset: z.string().nullable().optional(),
			updatedBy: z.string().uuid().nullable().optional(),
			updatedAt: z.string().nullable().optional(),
			updatedAtOffset: z.string().nullable().optional(),
			organizationId: z.string().uuid().optional(),
			subOrganizationId: z.string().uuid().optional(),
			jobTypeId: z.string().uuid().optional(),
			jobType: JobTypeInScopeSchema.optional(),
			subOrganization: z.unknown().optional(),
		}),
	);

const JobTypeInScopeSchema: z.ZodType<JobTypeInScope> = z.lazy(() =>
	z.object({
		id: z.string().uuid(),
		createdBy: z.string().uuid().nullable().optional(),
		createdAt: z.string().nullable().optional(),
		createdAtOffset: z.string().nullable().optional(),
		updatedBy: z.string().uuid().nullable().optional(),
		updatedAt: z.string().nullable().optional(),
		updatedAtOffset: z.string().nullable().optional(),
		deleted: z.boolean().optional(),
		organizationId: z.string().uuid(),
		subOrganizationJobTypes: z
			.array(SubOrganizationJobTypeInScopeSchema)
			.nullable()
			.optional(),
		name: z.string(),
		description: z.string().nullable().optional(),
		icon: z.string(),
		updatable: z.boolean().optional(),
		isDefault: z.boolean().optional(),
	}),
);

/**
 * OpenAPI `SubOrganizationTicketType` — items under `ticketType.subOrganizationTicketTypes`.
 */
export type SubOrganizationTicketTypeInScope = {
	id?: string;
	createdBy?: string | null;
	createdAt?: string | null;
	createdAtOffset?: string | null;
	updatedBy?: string | null;
	updatedAt?: string | null;
	updatedAtOffset?: string | null;
	organizationId?: string;
	subOrganizationId?: string;
	ticketTypeId?: string;
	ticketType?: TicketTypeInScope;
	subOrganization?: unknown;
};

/**
 * OpenAPI `TicketType` — nested on form-definition list rows (`swagger2.json`).
 */
export type TicketTypeInScope = {
	id: string;
	createdBy?: string | null;
	createdAt?: string | null;
	createdAtOffset?: string | null;
	updatedBy?: string | null;
	updatedAt?: string | null;
	updatedAtOffset?: string | null;
	deleted?: boolean;
	organizationId: string;
	subOrganizationTicketTypes?: SubOrganizationTicketTypeInScope[] | null;
	name: string;
	description?: string | null;
	icon: string;
	updatable?: boolean;
	isDefault?: boolean;
};

const SubOrganizationTicketTypeInScopeSchema: z.ZodType<SubOrganizationTicketTypeInScope> =
	z.lazy(() =>
		z.object({
			id: z.string().uuid().optional(),
			createdBy: z.string().uuid().nullable().optional(),
			createdAt: z.string().nullable().optional(),
			createdAtOffset: z.string().nullable().optional(),
			updatedBy: z.string().uuid().nullable().optional(),
			updatedAt: z.string().nullable().optional(),
			updatedAtOffset: z.string().nullable().optional(),
			organizationId: z.string().uuid().optional(),
			subOrganizationId: z.string().uuid().optional(),
			ticketTypeId: z.string().uuid().optional(),
			ticketType: TicketTypeInScopeSchema.optional(),
			subOrganization: z.unknown().optional(),
		}),
	);

const TicketTypeInScopeSchema: z.ZodType<TicketTypeInScope> = z.lazy(() =>
	z.object({
		id: z.string().uuid(),
		createdBy: z.string().uuid().nullable().optional(),
		createdAt: z.string().nullable().optional(),
		createdAtOffset: z.string().nullable().optional(),
		updatedBy: z.string().uuid().nullable().optional(),
		updatedAt: z.string().nullable().optional(),
		updatedAtOffset: z.string().nullable().optional(),
		deleted: z.boolean().optional(),
		organizationId: z.string().uuid(),
		subOrganizationTicketTypes: z
			.array(SubOrganizationTicketTypeInScopeSchema)
			.nullable()
			.optional(),
		name: z.string(),
		description: z.string().nullable().optional(),
		icon: z.string(),
		updatable: z.boolean().optional(),
		isDefault: z.boolean().optional(),
	}),
);

export type AssetTypeInScope = z.infer<typeof AssetTypeInScopeSchema>;

export {
	type ColumnValue,
	ColumnValueSchema,
	type ComparisonType,
	ComparisonTypeSchema,
	type CreateFormDefinitionInfo,
	CreateFormDefinitionInfoSchema,
	type DataQuery,
	DataQuerySchema,
	type EditFormDefinitionInfo,
	EditFormDefinitionInfoSchema,
	type EditFormLayoutInfo,
	EditFormLayoutInfoSchema,
	type EntityDataValidation,
	EntityDataValidationSchema,
	type FieldDefinition,
	FieldDefinitionSchema,
	type FormDefinition,
	FormDefinitionSchema,
	type FormDefinitionScopeQuery,
	FormDefinitionScopeQuerySchema,
	type FormDefinitionsListParams,
	FormDefinitionsListParamsSchema,
	type ScopeFields,
	ScopeFieldsSchema,
} from "./form-definition-core.types";

/**
 * OpenAPI `FormDefinitionWithScope` — list row / scoped definition (`GET /form-definitions/{start}/{count}`).
 * Nested scopes match `swagger2.json` `JobType`, `TicketType`, `AssetType` (including `icon`, offset strings, sub-org join arrays).
 */
export const FormDefinitionWithScopeSchema = FormDefinitionSchema.extend({
	organizationId: z.string().uuid(),
	jobType: JobTypeInScopeSchema.nullable().optional(),
	ticketType: TicketTypeInScopeSchema.nullable().optional(),
	assetType: AssetTypeInScopeSchema.nullable().optional(),
	subOrganizationId: z.string().uuid().nullable().optional(),
});

export type FormDefinitionWithScope = z.infer<
	typeof FormDefinitionWithScopeSchema
>;

const FormDefinitionWithScopeArraySchema = z.array(
	FormDefinitionWithScopeSchema,
);

/**
 * Custom error class for form-definition API operations.
 */
export class FormDefinitionApiError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		public readonly statusCode?: number,
	) {
		super(message);
		this.name = "FormDefinitionApiError";
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, FormDefinitionApiError);
		}
	}

	/**
	 * Builds an error from a failed {@link ServiceResult} (e.g. `isSuccess: false` on POST/PUT form-definition mutations).
	 */
	static fromServiceResult(result: ServiceResult) {
		return new FormDefinitionApiError(
			result.message || "Form definition operation failed",
			result,
		);
	}
}

/**
 * Normalizes GET /form-definitions/{start}/{count}: OpenAPI may document a single object; runtime may return an array.
 *
 * @throws {@link FormDefinitionApiError} When the payload is not a valid array or single `FormDefinitionWithScope`.
 */
export function parseFormDefinitionsPageResponse(
	data: unknown,
): FormDefinitionWithScope[] {
	const asArray = FormDefinitionWithScopeArraySchema.safeParse(data);
	if (asArray.success) return asArray.data;
	const asOne = FormDefinitionWithScopeSchema.safeParse(data);
	if (asOne.success) return [asOne.data];
	const detail = formatZodIssues(asOne.error) || formatZodIssues(asArray.error);
	throw new FormDefinitionApiError(
		`Invalid form definitions page response: ${detail}`,
		{ arrayParse: asArray.error, singleParse: asOne.error },
	);
}
